<template>
  <section v-if="error">
    <br/>
    <p class="error">{{ error }}</p>
  </section>

  <section class="grid-container" v-if="!error && loaded && task && task.taskStatus !== TASK_STATUS.SUCCESSFULLY_COMPLETED.value">
    <div>
      <br/>
        <WizardProgress :currentStep="stepIndex + 1" :totalSteps="steps.length" />
    </div>
    <div class="page-container grid-container wizard">
      <div class="grid-x grid-margin-x" >
        <component v-if="loaded" :key="stepIndex" :is="currentStepComponent" @next="nextStep" @prev="prevStep" ref="stepComponentRef" :formData="formData" L  @update-form="handleFormUpdate"/>

        <div v-if="stepIndex < steps.length - 1" id="wizard-navigation" class="cell small-12">
          <button class="btn btn-secondary" @click="prevStep" v-if="stepIndex != 0">Previous</button>
          <button class="btn btn-primary" @click="nextStep" :disabled="stepIndex === steps.length - 1">Next</button>
          <button class="btn btn-link" @click="finish">Cancel</button>
        </div>

        <div class="cell small-12">
          <button class="btn btn-primary" @click="finish" v-if="stepIndex == steps.length - 1">Finish</button>
        </div>


      </div>
    </div>
  </section>
  <section class="page-container grid-container" v-else> 
    <div class="grid-x grid-margin-x">
        <div class="cell small-12">
            <a @click="router.back()">← Back</a><br/><br/>
        </div>
    </div>
    <div class="grid-x grid-margin-x">
        <div class="cell small-12">
            <h1 class="text-2xl font-bold">Company Questionnaire</h1>
            <p>Thank you for completing your company questionnaire, it is currently being reviewed by our team.</p>
            <p>Below is a summary of the answers that you provided.</p>
          <table v-if="companyQuestionnaireResponse">
            <thead>
              <tr>
                <th>Answers Summary</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Payroll Scheme Type</td>
                <td>{{ payrollTypeDisplay }}</td>
              </tr>
              <tr>
                <td>Tax District</td>
                <td>{{ companyQuestionnaireResponse?.taxDistrict }}</td>
              </tr>
              <tr>
                <td>PAYE Reference</td>
                <td>{{ companyQuestionnaireResponse?.payeReference }}</td>
              </tr>
              <tr>
                <td>Accounts Office Reference</td>
                <td>{{ companyQuestionnaireResponse?.accountsOfficeReference }}</td>
              </tr>
              <tr>
                <td>Employment Allowance</td>
                <td>{{ formatToYesNo(companyQuestionnaireResponse?.hasEmploymentAllowance) }}</td>
              </tr>
              <tr v-if="companyQuestionnaireResponse?.hasEmploymentAllowance">
                <td>Employment Allowance Claimed</td>
                <td>
                  £{{ toTwoDecimalPlaces(Number(companyQuestionnaireResponse?.employmentAllowanceAlreadyClaimed) || 0) }}
                </td>
              </tr>
              <tr>
                <td>Apprenticeship Levy</td>
                <td>{{ formatToYesNo(companyQuestionnaireResponse.hasApprenticeshipLevy) }}</td>
              </tr>
              <tr v-if="companyQuestionnaireResponse?.hasApprenticeshipLevy">
                <td>Apprenticeship Levy Paid</td>
                <td>
                  £{{ toTwoDecimalPlaces(Number(companyQuestionnaireResponse?.apprenticeshipLevyAlreadyPaid) || 0) }}
                </td>
              </tr>
              <tr>
                <td>Small Employers Relief </td>
                <td>{{ formatToYesNo(companyQuestionnaireResponse.hasSmallEmployersRelief) }}</td>
              </tr>
            </tbody>
          </table>

        </div>
        <div class="cell small-12">
          <button class="btn btn-secondary" @click="router.back()">Return to Tasks</button>
        </div>
    </div>
  </section>
</template>


<script setup>

import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router';
import { formatToYesNo } from '@/helpers/boolean';
import { toTwoDecimalPlaces } from '@/helpers/numbers';
import { splitByCapitals } from '@/helpers/strings';

import { TASK_STATUS } from '@/helpers/constants';
import WizardProgress from '@/components/navigation/WizardProgressBar.vue';
import Step1 from './Company-Questionnaire-Step1.vue'
import Step2 from './Company-Questionnaire-Step2.vue'
import Step3 from './Company-Questionnaire-Step3.vue'
import Step4 from './Company-Questionnaire-Step4.vue'
import Step5 from './Company-Questionnaire-Step5.vue'
import Step6 from './Company-Questionnaire-Step6.vue'
import Step7 from './Company-Questionnaire-Step7.vue'
import Step8 from './Company-Questionnaire-Step8.vue'


const steps = [
  { label: 'Getting Started', component: Step1 },
  { label: 'Payroll Type', component: Step2 },
  { label: 'PAYE Reference', component: Step3 },
  { label: 'Accounts Office Reference', component: Step4 },
  { label: 'Employment Allowance', component: Step5 },
  { label: 'Apprenticeship Levy', component: Step6 },
  { label: 'Small Employers Relief', component: Step7 },
  { label: 'Complete', component: Step8 },
];
const stepIndex = ref(0)

const loaded = ref(false);

const stepComponentRef = ref(null)

const currentStepComponent = computed(() => steps[stepIndex.value].component)

const route = useRoute();
const router = useRouter();
const taskId = route.params.taskId;
const task = ref(null);
const companyQuestionnaireResponse = ref(null);

import { getDataCollectionTask } from '@/services/implementationmanagement.api';
import GetStartedStep5 from '../get-started/Get-Started-Step5.vue';
onMounted(async () => {

  var response = await getDataCollectionTask(taskId);

  if(response.status !== 200 || response.data === null || response.data === undefined) {
    error.value = errorString;
    return null;
  }

  task.value = response.data;

  try {

    // If dataJson is empty string then set accoridingly, otherwise parse it
    if(!task.value.dataJson || task.value.dataJson.trim() === "") {
      companyQuestionnaireResponse.value = "";
      loaded.value = true;
      return;
    }

    companyQuestionnaireResponse.value = JSON.parse(task.value.dataJson);

    loaded.value = true;
  } catch (e) {
    error.value = "Failed to parse questionnaire data";
    return;
  }
});


const nextStep = async () => {
  if (stepComponentRef.value && typeof stepComponentRef.value.validateStep === 'function') {
    const isValid = stepComponentRef.value.validateStep();

    if (!isValid) {
      return;
    }
  }

  if (stepComponentRef.value && typeof stepComponentRef.value.executeStep === 'function') {
    const success = await stepComponentRef.value.executeStep();
    if (!success) return;
  }

  error.value = null; // clear previous errors

  if (stepIndex.value < steps.length - 1) {
    stepIndex.value++;
  }
}

const prevStep = () => {
  if (stepIndex.value > 0) {
    stepIndex.value--
  }
}



const finish = () => {
  router.push({ name: 'DataCollection' });
}

const error = ref(null)

const payrollTypeDisplay = computed(() => {
  if (!companyQuestionnaireResponse.value) return 'Unknown';
  const type = companyQuestionnaireResponse.value.payrollType;
  return type === 'newscheme' ? 'New Scheme' : type === 'existingscheme' ? 'Existing Scheme' : 'Unknown';
});

const formData = ref({
  "employmentAllowance": false,
  "apprenticeshipLevy": false,
  "payrollType": ''
});
const handleFormUpdate = (updatedData) => {
  formData.value = updatedData;
};


</script>


<style scoped>
#wizard-breadcrumbs {
  margin-bottom: 40px;
  margin-top: 40px;
}

#wizard-navigation
{
  margin-top:30px;
}

</style>